import { faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import MultiSelect from 'components/common/MultiSelect';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import KbFoldersService from 'services/KbFolders.service';
import KbGroupsService from 'services/KbGroups.service';
import KbFolderDeletionModal from './KbFolderDeletionModal';

const KbFolderRegistrationModal = ({
  showModal,
  handleClose,
  isSuccess,
  refreshFolderDeleted,
  groups = [],
  isEditing = false,
  folderToEdit = null
}) => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [validated, setValidated] = useState(false);
  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedVisibleTo, setSelectedVisibleTo] = useState('ALL_AGENTS');

  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const initialState = {
    name: '',
    description: '',
    kbGroup: { id: null },
    visibleTo: 'ALL_AGENTS',
    agentsIds: [],
    customersIds: []
  };

  const [body, setBody] = useState(initialState);

  const [folderId, setFolderId] = useState(null);

  const [filters] = useState({
    page: 0,
    size: 200,
    includeInactives: false,
    name: ''
  });

  const form = useRef(null);

  const visibilityOptions = [
    { value: 'ALL_AGENTS', label: 'Todos os agentes' },
    { value: 'ALL_CUSTOMERS', label: 'Todos os clientes' },
    { value: 'AGENT', label: 'Agentes' },
    { value: 'CUSTOMER', label: 'Clientes' }
  ];

  // Formata os dados para o MultiSelect
  const formattedAgents = agents.map(agent => ({
    value: agent.id,
    label: agent.firstName + ' ' + agent.lastName
  }));

  const formattedCustomers = customers.map(customer => ({
    value: customer.id,
    label: customer.fantasyName || customer.name
  }));

  useEffect(() => {
    console.log(selectedVisibleTo);
  }, [selectedVisibleTo]);

  useEffect(() => {
    if (showModal) {
      if (isEditing && folderToEdit) {
        setBody({
          name: folderToEdit.name,
          description: folderToEdit.description || '',
          kbGroup: { id: folderToEdit.kbGroup.id || '' },
          visibleTo: folderToEdit.visibleTo,
          agentsIds: folderToEdit.agents
            ? folderToEdit.agents.map(item => item.id)
            : [],
          customersIds: folderToEdit.customers
            ? folderToEdit.customers.map(item => item.id)
            : []
        });

        setFolderId(folderToEdit.id || null);

        setSelectedVisibleTo(folderToEdit.visibleTo || '');

        if (folderToEdit.visibleTo === 'AGENT' && folderToEdit.agents) {
          const agentsData = folderToEdit.agents.map(item => ({
            value: item.id,
            label: item.firstName + ' ' + item.lastName
          }));
          setSelectedAgents(agentsData);
        }

        if (folderToEdit.visibleTo === 'CUSTOMER' && folderToEdit.customers) {
          const customersData = folderToEdit.customers.map(item => ({
            value: item.id,
            label: item.name
          }));
          setSelectedCustomers(customersData);
        }

        setValidated(false);
        setIsCreatingNewGroup(false);
      } else {
        clearFields();
        setValidated(false);
        setIsCreatingNewGroup(false);
      }
    }
  }, [showModal, folderToEdit, isEditing]);

  useEffect(() => {
    if (!showModal) return;

    getAgents();
    getCustomers();
  }, [showModal]);

  // Função para criar um novo grupo
  const createNewGroup = async () => {
    if (!newGroupName || newGroupName.trim() === '') {
      showErrorNotification('Por favor, insira um nome para o novo grupo.');
      return;
    }

    const newKbGroup = { name: newGroupName.trim() };
    const response = await KbGroupsService.create(newKbGroup);
    if (response.userMessage) {
      showErrorNotification(response.userMessage);
      return;
    }

    groups.push({
      id: response.id,
      name: response.name,
      description: response.description
    });
    showSuccessNotification('Grupo criado com sucesso');
    setIsCreatingNewGroup(false);
    setNewGroupName('');
    setBody(prevState => ({
      ...prevState,
      kbGroup: { id: response.id }
    }));
  };

  const getAgents = async () => {
    const response = await api.agents.listSummaryData();

    if (Array.isArray(response)) setAgents(response);
  };

  const getCustomers = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await api.customers.list(queryParams);
    response.content && setCustomers(response.content);
  };

  const clearFields = () => {
    setBody(initialState);
    setIsCreatingNewGroup(false);
    setNewGroupName('');
    setSelectedVisibleTo('');
    setSelectedAgents([]);
    setSelectedCustomers([]);
  };

  const handleCreateNewGroup = () => {
    setIsCreatingNewGroup(true);
  };

  const handleCancelNewGroup = () => {
    setIsCreatingNewGroup(false);
    setNewGroupName('');
  };
  const handleVisibilityChange = value => {
    console.log(value);
    setSelectedVisibleTo(value);

    setBody(prevState => {
      const newState = {
        ...prevState,
        visibleTo: value,
        agentsIds: value !== 'AGENT' ? [] : prevState.agentsIds,
        customersIds: value !== 'CUSTOMER' ? [] : prevState.customersIds
      };
      return newState;
    });

    // Limpar seleções anteriores ao mudar a visibilidade
    if (value !== 'AGENT') {
      setSelectedAgents([]);
    }

    if (value !== 'CUSTOMER') {
      setSelectedCustomers([]);
    }
  };

  const handleAgentSelection = values => {
    setSelectedAgents(values);
    setBody(prevState => ({
      ...prevState,
      agentsIds: values.map(item => item.value)
    }));
  };

  const handleCustomerSelection = values => {
    setSelectedCustomers(values);
    setBody(prevState => ({
      ...prevState,
      customersIds: values.map(item => item.value)
    }));
  };

  const onSubmit = async e => {
    e.preventDefault();

    if (form.current.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    const response = isEditing
      ? await KbFoldersService.update(folderToEdit.id, body)
      : await KbFoldersService.create(body);

    if (response.userMessage) {
      showErrorNotification(response.userMessage);
      return;
    }

    isSuccess(true);
    showSuccessNotification(
      isEditing ? 'Pasta atualizada com sucesso' : 'Pasta criada com sucesso'
    );
    clearFields();
    handleClose();
  };

  return (
    <>
      <KbFolderDeletionModal
        show={showConfirmDeleteModal}
        close={() => setShowConfirmDeleteModal(false)}
        folderId={folderId}
        folderName={body.name}
        isSuccess={refreshFolderDeleted}
      />
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Editar Pasta' : 'Nova Pasta'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form validated={validated} ref={form}>
            <Form.Group className="mb-3">
              <Form.Label>
                Nome <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="Inserir nome da pasta"
                autoFocus
                value={body.name}
                onChange={e =>
                  setBody(prevState => ({ ...prevState, name: e.target.value }))
                }
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira um nome.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="(opcional)"
                value={body.description}
                onChange={e =>
                  setBody(prevState => ({
                    ...prevState,
                    description: e.target.value
                  }))
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label>
                  Escolha um grupo <span className="text-danger"> *</span>
                </Form.Label>
                {!isCreatingNewGroup && !isEditing && (
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={handleCreateNewGroup}
                  >
                    Criar novo grupo
                  </Button>
                )}
              </div>

              {isCreatingNewGroup ? (
                <>
                  <Flex direction={'column'}>
                    <Form.Control
                      type="text"
                      value={newGroupName}
                      onChange={e => setNewGroupName(e.target.value)}
                      placeholder="Nome do novo grupo"
                    />

                    <Flex className={'w-100 mt-2'} justifyContent={'end'}>
                      <Button
                        size="sm"
                        variant="falcon-default"
                        className="me-2"
                        onClick={handleCancelNewGroup}
                      >
                        Cancelar
                      </Button>
                      <Button size="sm" onClick={createNewGroup}>
                        Salvar
                      </Button>
                    </Flex>
                  </Flex>
                </>
              ) : (
                <Form.Select
                  value={body.kbGroup.id || null}
                  onChange={e =>
                    setBody(prevState => ({
                      ...prevState,
                      kbGroup: { id: parseInt(e.target.value) }
                    }))
                  }
                  required
                >
                  <option value={null}>Selecione um grupo</option>
                  {groups.map((group, index) => (
                    <option key={index} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Visível para <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Select
                value={selectedVisibleTo}
                onChange={e => handleVisibilityChange(e.target.value)}
              >
                {visibilityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {selectedVisibleTo === 'AGENT' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Selecionar agentes <span className="text-danger"> *</span>
                </Form.Label>
                <MultiSelect
                  placeholder="Selecione os agentes"
                  options={formattedAgents}
                  value={selectedAgents}
                  onChange={handleAgentSelection}
                  isMulti
                />
              </Form.Group>
            )}

            {selectedVisibleTo === 'CUSTOMER' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Selecionar clientes <span className="text-danger"> *</span>
                </Form.Label>
                <MultiSelect
                  placeholder="Selecione os clientes"
                  options={formattedCustomers}
                  value={selectedCustomers}
                  onChange={handleCustomerSelection}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer
          className={`d-flex justify-content-${isEditing ? 'between' : 'end'}`}
        >
          {isEditing && (
            <Col xs="auto">
              <Button
                className="text-danger"
                variant="falcon-default"
                onClick={() => {
                  setShowConfirmDeleteModal(true);
                  handleClose();
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </Col>
          )}
          <Row xs="auto">
            <Button variant="falcon-default" onClick={handleClose}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={onSubmit} className="ms-2">
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default KbFolderRegistrationModal;
